/**
 * Editorial masthead — institutional byline.
 *
 * 2026-02-10 pass 71 — Refactored from named-persona model (Jordan Mercer /
 * Riley Okafor) to a single institutional editorial byline. Direct response
 * to the AdSense E-E-A-T anonymous-publisher framework decision (see
 * /editorial-standards): trust signals come from the publication, its
 * sourcing, and corrections record — not from individual personas.
 *
 * All downstream callsites (GuidePage, CalculatorPage, QaPage,
 * StateRebatePage) flow through `bylineForTopic()` so they keep working
 * unchanged. The Byline component detects when author === reviewer and
 * renders a single institutional line ("By HavenCostGuide Editorial Team")
 * rather than the duplicated "Written by X / Reviewed by Y" pattern.
 */

export const HAVEN_EDITORIAL_TEAM = {
  id: "editorial-team",
  name: "HavenCostGuide Editorial Team",
  role: "Independent editorial team",
  bio: "HavenCostGuide is published by RudderBay, a sole proprietorship. The Editorial Team researches U.S. home renovation cost data, writes guides, reviews methodology, and maintains the corrections record under the standards documented on the Editorial Standards page.",
  expertise: [
    "Residential renovation cost modeling",
    "State labor-cost indices + permit data",
    "Utility rebate + energy-program coverage",
    "Insurance claim valuation",
  ],
  profile: "/editorial-standards",
};

// Single AUTHORS source so any imports referencing the old keys still work.
// All keys point to the same institutional team entity.
export const AUTHORS = {
  team: HAVEN_EDITORIAL_TEAM,
  // Aliases preserved for backwards-compatibility with any straggler imports.
  jordan: HAVEN_EDITORIAL_TEAM,
  riley: HAVEN_EDITORIAL_TEAM,
};

/**
 * Default byline — same team appears as both author and reviewer. The Byline
 * component handles deduplication at render time.
 */
export const DEFAULT_BYLINE = {
  author: HAVEN_EDITORIAL_TEAM,
  reviewer: HAVEN_EDITORIAL_TEAM,
};

/**
 * Topic-based overrides — kept as a stub for API compatibility but every
 * topic now resolves to the same institutional pair. Adding topic-specific
 * named experts in the future (if anonymity preference changes) only requires
 * editing this map.
 */
export const BYLINE_BY_TOPIC = {
  rebates: DEFAULT_BYLINE,
  energy: DEFAULT_BYLINE,
  heehra: DEFAULT_BYLINE,
  insurance: DEFAULT_BYLINE,
  permits: DEFAULT_BYLINE,
  cost: DEFAULT_BYLINE,
};

/** Look up a byline pair by topic, falling back to the default. */
export function bylineForTopic(topic) {
  return BYLINE_BY_TOPIC[topic] || DEFAULT_BYLINE;
}
